import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    CartProductType,
    updateCartCache,
    MY_CART_DETAILS,
    handleCartError,
    fetchDetails,
    useAddItemToCart,
    useCartDetails,
} from '@steelbuy/data-access';
import { useModal } from '@steelbuy/modal-dialog';
import { CartBuyerModel, ListingBuyerModel, PackageBuyerModel } from '@steelbuy/ts-shared';
import { toaster } from '@steelbuy/ui-primitive';
import { AddToCartConfirmModal } from '../components/add-to-cart-confirm-modal/AddToCartConfirmModal';
import { useCheckoutContext } from '../components/checkout/CheckoutContext';
import { OfferInProgressModal } from '../components/listing-details/OfferInProgressModal';
import { RoutePath } from '../router/Routes';

export const useBuyNowAndAddToCart = ({
    isPackage,
    checkoutPath,
    checkoutBackPath,
}: {
    isPackage: boolean;
    checkoutPath: RoutePath;
    checkoutBackPath: RoutePath;
}) => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const [cartData, setCartData] = useState<CartBuyerModel>();
    const [listingIdAddingToCart, setListingIdAddingToCart] = useState<string>('');
    const addItemToCartMutation = useAddItemToCart();
    const openAddToCartModal = useModal();
    const offerInProgressModal = useModal();
    const queryClient = useQueryClient();
    const { refetch } = useCartDetails({ queryOptions: { enabled: false } });
    const checkoutContext = useCheckoutContext();
    const [isBuyingListingId, setIsBuyingListingId] = useState('');
    const navigate = useNavigate();
    const listingIdRef = useRef<string>('');
    const buyNowRef = useRef<boolean>(false);

    const addToCart = async (id: string): Promise<void> => {
        offerInProgressModal.hide();
        setListingIdAddingToCart(id);
        addItemToCartMutation.mutate(
            { productId: id, productType: isPackage ? CartProductType.PACKAGE : CartProductType.LISTING },
            {
                onSuccess: (response) => {
                    setCartData(response);
                    openAddToCartModal.show();
                    setListingIdAddingToCart('');
                    updateCartCache(queryClient, [MY_CART_DETAILS], response);
                },
                onError: (e: unknown) => {
                    setListingIdAddingToCart('');
                    handleCartError(e, refetch, t);
                },
            }
        );
    };

    const handleCheckout = async (id: string) => {
        offerInProgressModal.hide();
        checkoutContext.initializeWithListingId(id);
        setIsBuyingListingId(id);
        const listing = await fetchDetails(id, isPackage);
        if (listing.canCheckout) {
            navigate(checkoutPath, {
                state: checkoutBackPath,
            });
        } else {
            toaster.warn(t('application.addToCartConfirmModal.insufficientCredit'));
        }
        setIsBuyingListingId('');
    };

    const handleBuyNow = (listing: ListingBuyerModel | PackageBuyerModel) => {
        buyNowRef.current = true;
        if ((listing as ListingBuyerModel).negotiationExists) {
            offerInProgressModal.show();
            listingIdRef.current = listing.id;
        } else {
            handleCheckout(listing.id);
        }
    };

    const handleAddToCart = (listing: PackageBuyerModel | ListingBuyerModel) => {
        buyNowRef.current = false;
        if ((listing as ListingBuyerModel).negotiationExists) {
            offerInProgressModal.show();
            listingIdRef.current = listing.id;
        } else {
            addToCart(listing.id);
        }
    };

    const PurchaseModals = () => (
        <>
            {cartData && <AddToCartConfirmModal modalAccessor={openAddToCartModal} cartData={cartData} />}
            <OfferInProgressModal
                offerInProgressModal={offerInProgressModal}
                header={
                    buyNowRef.current
                        ? t('application.offers.buyNowModalheader')
                        : t('application.offers.addToCartModalHeader')
                }
                handleBuyNow={() =>
                    buyNowRef.current ? handleCheckout(listingIdRef.current) : addToCart(listingIdRef.current)
                }
                buttonLabel={buyNowRef.current ? t('application.offers.buynow') : t('uiDomain:commonList.addToCart')}
            />
        </>
    );

    return { PurchaseModals, isBuyingListingId, handleAddToCart, handleBuyNow, listingIdAddingToCart };
};
